import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import AdminCreditsService from '../../../services/adminCreditsService';
import { History, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';

const CreditHistoryModal = ({ employer, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await AdminCreditsService.getUserCreditHistory(employer.id, 50);
      
      if (result.success) {
        setHistory(result.data);
      } else {
        setError(result.error || 'Failed to load credit history');
      }
    } catch (err) {
      setError('An error occurred while loading history');
      console.error('Load history error:', err);
    } finally {
      setLoading(false);
    }
  }, [employer.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'failed':
        return <XCircle size={16} className="text-red-500" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-500" />;
      default:
        return <History size={16} className="text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="AI Generation History"
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        {/* Employer Info */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Employer</p>
            <p className="text-lg font-semibold text-gray-900">
              {employer.full_name || employer.email}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-700">Total Generations</p>
            <p className="text-lg font-semibold text-gray-900">
              {employer.ai_generation_count || 0}
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* History List */}
        {!loading && !error && (
          <div className="max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <History size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No generation history found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item.status)}
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(item.status)}`}>
                          {item.status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(item.created_at)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-600">AI Model</p>
                        <p className="text-sm font-medium text-gray-900">
                          {item.ai_model || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Tokens Used</p>
                        <p className="text-sm font-medium text-gray-900">
                          {item.tokens_used || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Cost</p>
                        <p className="text-sm font-medium text-gray-900">
                          ${((item.cost_cents || 0) / 100).toFixed(4)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Credits Used</p>
                        <p className="text-sm font-medium text-gray-900">
                          1 credit
                        </p>
                      </div>
                    </div>

                    {/* Brief Data (if available) */}
                    {item.brief_data && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-medium">
                            View Generation Brief
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 max-h-40 overflow-y-auto">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                              {typeof item.brief_data === 'string' 
                                ? item.brief_data 
                                : JSON.stringify(item.brief_data, null, 2)}
                            </pre>
                          </div>
                        </details>
                      </div>
                    )}

                    {/* Error Message (if failed) */}
                    {item.status === 'failed' && item.error_message && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-red-600">
                          <span className="font-semibold">Error:</span> {item.error_message}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        {!loading && !error && history.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={18} className="text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Summary</p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-blue-700">Total Generations</p>
                <p className="text-lg font-bold text-blue-900">{history.length}</p>
              </div>
              <div>
                <p className="text-xs text-blue-700">Successful</p>
                <p className="text-lg font-bold text-green-600">
                  {history.filter(h => h.status === 'success').length}
                </p>
              </div>
              <div>
                <p className="text-xs text-blue-700">Failed</p>
                <p className="text-lg font-bold text-red-600">
                  {history.filter(h => h.status === 'failed').length}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreditHistoryModal;
